import React, { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import NoteAddOutlined from '@mui/icons-material/NoteAddOutlined';
import FileCopyOutlined from '@mui/icons-material/FileCopyOutlined';

const AddTemplateModal = ({
    isOpen,
    onClose,
    onSelect,
    anchorEl,
    offset = 10,
    horizontalOffset = 0
}) => {
    const cardButtonRef = useRef(null);

    if (!isOpen) return null;

    const handleFromCardClick = () => {
        // Close the first modal and signal to open card selection
        onClose();
        // Pass a callback to parent to open card modal
        if (onSelect) {
            onSelect('open-card-modal');
        }
    };

    const getModalPosition = () => {
        if (!anchorEl) {
            // Center on screen if no anchor
            return {
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
            };
        }

        const anchorRect = anchorEl.getBoundingClientRect();
        const modalWidth = 384; // maxWidth: '24rem' = 384px
        const modalHeight = 200; // Approximate height
        const margin = 16; // Minimum margin from screen edges
        const rightMargin = 24; // Extra margin on the right side

        // Calculate initial position
        let modalTop = anchorRect.bottom + offset;
        let modalLeft = anchorRect.left + (anchorRect.width / 2) + horizontalOffset;

        // Check viewport boundaries
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Adjust horizontal position to stay within viewport
        if (modalLeft - modalWidth / 2 < margin) {
            modalLeft = margin + modalWidth / 2;
        } else if (modalLeft + modalWidth / 2 > viewportWidth - rightMargin) {
            modalLeft = viewportWidth - rightMargin - modalWidth / 2;
        }

        // Adjust vertical position to stay within viewport
        if (modalTop + modalHeight > viewportHeight - margin) {
            // If not enough space below, position above the button
            modalTop = anchorRect.top - modalHeight - offset;
            // If still not enough space, center vertically
            if (modalTop < margin) {
                modalTop = (viewportHeight - modalHeight) / 2;
            }
        }

        // Ensure minimum margin from top
        if (modalTop < margin) {
            modalTop = margin;
        }

        return {
            top: `${modalTop}px`,
            left: `${modalLeft}px`,
            transform: 'translateX(-50%)',
        };
    };

    const position = getModalPosition();

    const handleOverlayClick = (e) => {
        e.stopPropagation();
        onClose();
    };

    const boxStyles = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #e0e0e0',
        borderRadius: '0.5rem',
        padding: '1.25rem',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        width: '10rem',
        height: '6rem',
        boxSizing: 'border-box',
        '&:hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#828282',
        },
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1100,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
            }}
            onClick={handleOverlayClick}
        >
            <Box
                sx={{
                    position: 'absolute',
                    ...position,
                    backgroundColor: '#fff',
                    borderRadius: '0.5rem',
                    paddingTop: '2rem',
                    paddingBottom: '2rem',
                    paddingLeft: '2rem',
                    paddingRight: '2rem',
                    width: 'auto',
                    maxWidth: '24rem',
                    minWidth: '320px',
                    textAlign: 'center',
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08), 0px 2px 8px rgba(0, 0, 0, 0.04)',
                    zIndex: 1101,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <Typography
                    variant="h6"
                    sx={{
                        textAlign: 'center',
                        marginBottom: '1rem',
                        fontWeight: 600,
                        fontSize: '1rem'
                    }}
                >
                    Select a starting point
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        gap: '0.75rem',
                        width: '100%',
                        justifyContent: 'center',
                        marginTop: '1rem'
                    }}
                >
                    <Box
                        sx={boxStyles}
                        onClick={() => onSelect('from-scratch')}
                    >
                        <NoteAddOutlined
                            sx={{
                                fontSize: '2rem',
                                color: '#828282',
                                marginBottom: '1rem'
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                textAlign: 'center',
                                fontSize: '0.875rem'
                            }}
                        >
                            From Scratch
                        </Typography>
                    </Box>

                    <Box
                        ref={cardButtonRef}
                        sx={boxStyles}
                        onClick={handleFromCardClick}
                    >
                        <FileCopyOutlined
                            sx={{
                                fontSize: '2rem',
                                color: '#828282',
                                marginBottom: '1rem'
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                textAlign: 'center',
                                fontSize: '0.875rem'
                            }}
                        >
                            From existing card
                        </Typography>
                    </Box>
                </Box>
            </Box>

        </Box>
    );
};

export default AddTemplateModal;

